import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, MapPin, Users, Check, HelpCircle, X, Share2, Copy, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import ConsumerLayout from '@/components/ConsumerLayout';
import { venueUrl } from '@/lib/urls';
import api from '@/lib/api';
import { toast } from 'sonner';
import { format } from 'date-fns';

const STATUS_CONFIG = {
  going: { label: 'Going', icon: Check, color: 'bg-green-100 text-green-700', btnColor: 'bg-green-600 hover:bg-green-700' },
  maybe: { label: 'Maybe', icon: HelpCircle, color: 'bg-amber-100 text-amber-700', btnColor: 'bg-amber-500 hover:bg-amber-600' },
  declined: { label: "Can't make it", icon: X, color: 'bg-red-100 text-red-700', btnColor: 'bg-red-500 hover:bg-red-600' },
};

export default function PlanView() {
  const { planId } = useParams();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guestName, setGuestName] = useState('');
  const [rsvpStatus, setRsvpStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get(`/plans/${planId}`).then(res => setPlan(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, [planId]);

  const handleRsvp = async (status) => {
    if (!guestName.trim()) { toast.error('Enter your name'); return; }
    setSubmitting(true);
    try {
      await api.post(`/plans/${planId}/rsvp`, { name: guestName, status });
      setRsvpStatus(status);
      toast.success(status === 'going' ? "You're in!" : status === 'maybe' ? 'Noted as maybe' : 'Noted');
      // Refresh plan
      const res = await api.get(`/plans/${planId}`);
      setPlan(res.data);
    } catch { toast.error('Failed'); }
    finally { setSubmitting(false); }
  };

  const shareUrl = `${window.location.origin}/plans/${plan?.share_code || planId}`;

  if (loading) return <ConsumerLayout><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div></ConsumerLayout>;
  if (!plan) return <ConsumerLayout><div className="text-center py-20 text-muted-foreground">Plan not found</div></ConsumerLayout>;

  const going = (plan.guests || []).filter(g => g.status === 'going');
  const maybe = (plan.guests || []).filter(g => g.status === 'maybe');

  return (
    <ConsumerLayout>
      <div className="max-w-2xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="text-center mb-8">
          <Badge className="mb-3 bg-indigo-100 text-indigo-700">EVENT★GOERS Plan</Badge>
          <h1 className="text-3xl font-bold mb-2">{plan.title}</h1>
          <p className="text-muted-foreground">Created by {plan.creator_name}</p>
        </div>

        {/* Venue Card */}
        {plan.venue && (
          <Link to={venueUrl(plan.venue)}>
            <Card className="mb-6 overflow-hidden hover:shadow-lg transition-shadow">
              {plan.venue.images?.[0] && (
                <div className="aspect-video max-h-48"><img src={plan.venue.images[0]} alt="" className="w-full h-full object-cover" /></div>
              )}
              <CardContent className="p-4">
                <h2 className="font-bold text-lg">{plan.venue.name || plan.venue_name}</h2>
                {plan.venue.address && <p className="text-sm text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" /> {plan.venue.address}</p>}
              </CardContent>
            </Card>
          </Link>
        )}

        {/* Date & Time */}
        <div className="flex gap-4 mb-6">
          {plan.date && (
            <div className="flex items-center gap-2 text-sm"><Calendar className="w-4 h-4 text-indigo-600" /><span>{format(new Date(plan.date + 'T12:00:00'), 'EEEE, MMMM d, yyyy')}</span></div>
          )}
          {plan.time && (
            <div className="flex items-center gap-2 text-sm"><Clock className="w-4 h-4 text-indigo-600" /><span>{plan.time}</span></div>
          )}
        </div>

        {plan.message && <p className="text-muted-foreground mb-6">{plan.message}</p>}

        {/* Guest List */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2"><Users className="w-5 h-5 text-indigo-600" /> Who's In ({going.length} going{maybe.length > 0 ? `, ${maybe.length} maybe` : ''})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {(plan.guests || []).map((guest, i) => {
              const config = STATUS_CONFIG[guest.status] || STATUS_CONFIG.going;
              return (
                <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-sm">{guest.name?.charAt(0) || '?'}</div>
                    <span className="font-medium text-sm">{guest.name}</span>
                    {i === 0 && <Badge variant="outline" className="text-[10px]">Host</Badge>}
                  </div>
                  <Badge className={config.color + ' text-xs'}>{config.label}</Badge>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* RSVP */}
        {!rsvpStatus ? (
          <Card className="mb-6">
            <CardContent className="p-6 space-y-4">
              <h3 className="font-bold text-center">Are you in?</h3>
              <Input placeholder="Your name" value={guestName} onChange={e => setGuestName(e.target.value)} className="text-center" />
              <div className="flex gap-3">
                {Object.entries(STATUS_CONFIG).map(([status, config]) => (
                  <Button key={status} className={`flex-1 ${config.btnColor} text-white`} disabled={submitting || !guestName.trim()} onClick={() => handleRsvp(status)}>
                    <config.icon className="w-4 h-4 mr-1" /> {config.label}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="text-center py-4 mb-6"><Check className="w-8 h-8 text-green-500 mx-auto mb-2" /><p className="font-bold">RSVP Saved!</p></div>
        )}

        {/* Share */}
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-2"><Share2 className="w-5 h-5 text-indigo-600" /><span className="font-medium text-sm">Share this plan</span></div>
            <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); toast.success('Link copied!'); }}>
              {copied ? <><Check className="w-4 h-4 mr-1" /> Copied</> : <><Copy className="w-4 h-4 mr-1" /> Copy Link</>}
            </Button>
          </CardContent>
        </Card>
      </div>
    </ConsumerLayout>
  );
}
